import { Box } from "@mui/material";
import {
  Button,
  ErrorBoundary,
  PrimaryBar,
  Typography,
} from "@platform/design-system";
import { useSignedInUser, useUser } from "@platform/user/client";
import { Outlet, useLocation } from "react-router";

import type { ReactNode } from "react";

export const APP_LAYOUT_TEST_ID = "golden-layout";

const TABS = [
  { label: "Cases", path: "/" },
  { label: "Review queue", path: "/review" },
  { label: "Test groups", path: "/groups" },
];

// The tab each top-level path belongs under: a case under Cases, a suite under its group.
const TAB_BY_SECTION: Readonly<Record<string, string>> = {
  "": "/",
  cases: "/",
  review: "/review",
  groups: "/groups",
  suites: "/groups",
};

export const tabFor = (pathname: string): string | false =>
  TAB_BY_SECTION[pathname.split("/")[1] ?? ""] ?? false;

const UserMenu = (): ReactNode => {
  const { displayName } = useSignedInUser();
  const { endSession } = useUser();

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
      <Typography
        variant="body-medium"
        data-testid={`${APP_LAYOUT_TEST_ID}-user`}
      >
        {displayName}
      </Typography>
      <Button
        variant="tertiary"
        size="sm"
        data-testid={`${APP_LAYOUT_TEST_ID}-sign-out`}
        onClick={() => void endSession("user")}
      >
        Sign out
      </Button>
    </Box>
  );
};

/** The navigation bar over every page of the app. */
export const AppLayout = (): ReactNode => {
  const { pathname } = useLocation();

  return (
    <Box data-testid={APP_LAYOUT_TEST_ID}>
      <PrimaryBar
        tabs={TABS}
        currentTab={tabFor(pathname)}
        extras={<UserMenu />}
      />
      <Box component="main" sx={{ px: 4, py: 3, maxWidth: 1280, mx: "auto" }}>
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </Box>
    </Box>
  );
};
